import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dropdown, Form, Input, Modal, Table, Avatar } from 'antd';
import { Icon as CroplerIcon, Tag as CroplerTag, Button } from '@cropler/ui-kit';

import { useDeleteWorkspaceMutation, useUpdateWorkspaceMutation } from '../../store/api/workspaces';
import { setAvatarCompany, setCompanyName, setCurrentUserWorkspaces } from '../../store/slices/auth';
import { membershipsApi, useDeleteWorkspaceMembershipMutation } from '../../store/api/memberships';
import { setCurrentWorkspaceId } from '../../store/slices/workspaces';
import { sensorsApi } from '../../store/api/sensors';
import { fieldsApi } from '../../store/api/fields';
import { userApi } from '../../store/api/user';

import { useLocalStorage } from '../../hooks';
import { Workspace } from '../../constants/keys';

import styles from './styles.module.scss';
import stylesDropdownMenu from '../../styles/dropdownMenu.module.scss';
import stylesModal from '../../styles/modal.module.scss';
import { Organization } from '../WorkspaceModal/Organization.jsx';
import { getFirstLetter } from '../../utils/ui.tsx';
import UploadAvatar from '../UploadAvatar/index.jsx';

export const Workspaces = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const columns = [
    {
      title: t('labels.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('labels.role'),
      dataIndex: 'role',
      key: 'role',
      align: 'left',
      width: '200px',
    },
    {
      title: ' ',
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      width: '120px',
    },
    {
      title: ' ',
      dataIndex: 'actions',
      key: 'actions',
      width: '56px',
    },
  ];

  const [formRename] = Form.useForm();

  const [, setWorkspaceId] = useLocalStorage(Workspace);

  const { currentUserWorkspaces } = useSelector((state) => state.auth);

  const [data, setData] = useState([]);
  const [activeAddWorkspace, setActiveAddWorkspace] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState({ id: '', name: '', avatar: '' });
  const [avatarFile, setAvatarFile] = useState('');
  const [currentMember, setCurrentMember] = useState('');
  const [renameModalOpened, setRenameModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [deleteMemberView, setDeleteMemberView] = useState(true);

  const [updateWorkspace, { isSuccess: isUpdateWorkspaceSuccess }] = useUpdateWorkspaceMutation();
  const [deleteWorkspaceMember, { isSuccess: isSuccessDeleteMember }] = useDeleteWorkspaceMembershipMutation();
  const [deleteWorkspace] = useDeleteWorkspaceMutation();

  const onOpenAddPanel = () => {
    setActiveAddWorkspace(!activeAddWorkspace);
  };

  const onOpenRenameModal = ({ id, name, avatar }) => {
    setRenameModalOpened(true);
    formRename.setFieldValue('workspaceName', name);
    setCurrentWorkspace({ id, name, avatar });
    setAvatarFile('');
  };

  const changeImage = (file) => {
    setAvatarFile(file ? file : '');
  };

  const onOpenDeleteModal = ({ role, memberId, workspace }) => {
    setDeleteMemberView(role !== 'owner');
    setCurrentMember(memberId);
    setCurrentWorkspace({ id: workspace.id, name: workspace.name });
    setDeleteModalOpened(true);
  };

  const onUpdateWorkspace = () => {
    const newName = formRename.getFieldValue('workspaceName');
    const args = { workspaceId: currentWorkspace.id };
    if (newName && newName !== currentWorkspace.name) {
      args.name = newName;
    }
    if (avatarFile !== currentWorkspace.avatar) {
      args.avatar = avatarFile;
    }
    if (newName !== currentWorkspace.name || avatarFile !== currentWorkspace.avatar) {
      updateWorkspace(args);
    }
    setRenameModalOpened(false);
  };

  const deleteMember = () => {
    deleteWorkspaceMember({ workspaceMembershipId: currentMember });
    setDeleteModalOpened(false);
  };

  const onDeleteWorkspace = () => {
    deleteWorkspace({ id: currentWorkspace.id });
    let list = [];
    currentUserWorkspaces.map((item) => {
      if (currentWorkspace.id !== item.workspace.id) {
        list.push(item);
      }
    });
    dispatch(setCurrentUserWorkspaces({ memberships: list }));
    setDeleteModalOpened(false);
  };

  const goToSettings = useCallback(
    ({ id, name, avatar }) => {
      setWorkspaceId(id);
      dispatch(setCurrentWorkspaceId({ workspaceId: id }));
      dispatch(setCompanyName({ userCompany: name }));
      dispatch(setAvatarCompany({ avatar: avatar }));
      setTimeout(() => {
        dispatch(sensorsApi.util.resetApiState());
        dispatch(membershipsApi.util.resetApiState());
        dispatch(fieldsApi.util.resetApiState());
        navigate(`/devices`, { replace: true });
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (currentUserWorkspaces) {
      const list = [];
      currentUserWorkspaces.map((item, index) => {
        list.push({
          key: (index + 1).toString(),
          name: (
            <div className={styles.workspacesTableName}>
              <Avatar size={'small'} src={item.workspace.avatar} alt={item.workspace.name}>
                {getFirstLetter(item.workspace.name)}
              </Avatar>
              {item.workspace.name}
            </div>
          ),
          role: <div className={styles.workspacesTableRole}>{t(`roles.${item.role}`)}</div>,
          status:
            item.role === 'owner' ? (
              <CroplerTag type={'success'}>{t('common.personal')}</CroplerTag>
            ) : (
              <CroplerTag type={'progress'}>{t('common.invited')}</CroplerTag>
            ),
          actions: (
            <div className={styles.devicesTableNav}>
              <Dropdown
                menu={{
                  items: [
                    // Edit
                    {
                      key: '1',
                      label: (item.role === 'editor' || item.role === 'owner') && (
                        <Button
                          type="default"
                          className={stylesDropdownMenu.dropdownMenuLink}
                          onClick={() =>
                            onOpenRenameModal({
                              id: item.workspace.id,
                              name: item.workspace.name,
                              avatar: item.workspace.avatar,
                            })
                          }
                        >
                          <CroplerIcon name="editLine" />
                          <span>{t('actions.edit')}</span>
                        </Button>
                      ),
                    },
                    // Settings
                    {
                      key: '2',
                      label: (
                        <Button
                          type="default"
                          className={stylesDropdownMenu.dropdownMenuLink}
                          onClick={() =>
                            goToSettings({
                              id: item.workspace.id,
                              name: item.workspace.name,
                              avatar: item.workspace.avatar,
                            })
                          }
                        >
                          <CroplerIcon name="settings" />
                          <span>{t('actions.settings')}</span>
                        </Button>
                      ),
                    },
                    // Delete/Leave
                    {
                      key: '3',
                      label: (
                        <Button
                          type="default-danger"
                          className={stylesDropdownMenu.dropdownMenuLink}
                          onClick={() =>
                            onOpenDeleteModal({ role: item.role, memberId: item.id, workspace: item.workspace })
                          }
                        >
                          <CroplerIcon name="remove" />
                          <span>{item.role === 'owner' ? t('actions.delete') : t('actions.leave')}</span>
                        </Button>
                      ),
                    },
                  ],
                }}
              >
                <span>
                  <Button size="small" icon={<CroplerIcon name="more" />} type="default"></Button>
                </span>
              </Dropdown>
            </div>
          ),
        });
      });

      setData(list);
    }
  }, [currentUserWorkspaces]);

  useEffect(() => {
    if (isUpdateWorkspaceSuccess) {
      dispatch(userApi.endpoints.getCurrentUser.initiate(undefined, { forceRefetch: true, subscribe: false }));
    }
  }, [isUpdateWorkspaceSuccess]);

  useEffect(() => {
    if (isSuccessDeleteMember) {
      let list = [];
      currentUserWorkspaces.map((item) => {
        if (currentMember === item.id) {
          list.push(item);
        }
      });
      dispatch(setCurrentUserWorkspaces({ memberships: list }));
    }
  }, [isSuccessDeleteMember]);

  return (
    <>
      <div className={styles.workspaces}>
        <div className={styles.workspacesHeader}>
          <h2 className={styles.workspacesTitle}>{t('workspaces.title')}</h2>
          <Button type="secondary" icon={<CroplerIcon name="add" />} onClick={() => onOpenAddPanel()}>
            {t('workspaces.buttonAdd')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          rootClassName={`${styles.workspacesTable} workspacesTable`}
        />
      </div>

      {/* Add workspace */}
      <Modal
        open={activeAddWorkspace}
        onCancel={() => setActiveAddWorkspace(false)}
        footer={false}
        centered
        width={450}
      >
        <Organization setActiveModal={() => setActiveAddWorkspace(false)} showOnlyCreateForm={true} t={t} />
      </Modal>

      {/* Edit workspace */}
      <Modal
        title={t('workspaces.update.title')}
        open={renameModalOpened}
        onCancel={() => setRenameModalOpened(false)}
        footer={false}
        centered
        width={450}
      >
        <div className={styles.workspacesFormItem}>
          <UploadAvatar imageUrl={currentWorkspace.avatar} changeImage={changeImage} text={currentWorkspace.name} />
        </div>
        <Form form={formRename} layout="vertical" requiredMark={false}>
          <Form.Item
            label={t('workspaces.update.form.name')}
            name="workspaceName"
            rules={[
              {
                required: true,
                type: 'string',
                max: 128,
                min: 1,
                message: t('workspaces.update.form.nameMessage'),
              },
            ]}
          >
            <Input type="text" size="large" />
          </Form.Item>

          <div className={stylesModal.modalNav}>
            <Button type="secondary" onClick={() => setRenameModalOpened(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="primary"
              icon={<CroplerIcon name="check" />}
              htmlType="submit"
              onClick={() => onUpdateWorkspace()}
            >
              {t('actions.save')}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Delete member/workspace */}
      <Modal
        title={t('common.areYouSure')}
        open={deleteModalOpened}
        onCancel={() => setDeleteModalOpened(false)}
        footer={false}
        centered
        width={450}
      >
        {deleteMemberView ? (
          <p>{t('workspaces.delete.memberText')}</p>
        ) : (
          <p
            dangerouslySetInnerHTML={{
              __html: t('workspaces.delete.workspaceText', {
                name: currentWorkspace.name,
              }),
            }}
          ></p>
        )}
        <div className={stylesModal.modalNav}>
          <Button type="secondary" onClick={() => setDeleteModalOpened(false)}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="danger"
            icon={<CroplerIcon name="check" />}
            onClick={() => (deleteMemberView ? deleteMember() : onDeleteWorkspace())}
          >
            {deleteMemberView ? t('actions.leave') : t('actions.delete')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
